import { openDB, DBSchema, IDBPDatabase } from 'idb'
import { CheckRecord, Report, CheckConfig, CurrentCheckState, DepartmentConfig } from '@/types'

// IndexedDB 数据库结构
interface GasInspectionDB extends DBSchema {
  check_records: {
    key: string
    value: CheckRecord
    indexes: { 'by-date': Date; 'by-unit': string }
  }
  reports: {
    key: string
    value: Report
    indexes: { 'by-record-id': string }
  }
}

// 数据库名称和版本
const DB_NAME = 'gas-inspection-db'
const DB_VERSION = 1

// 存储键名
const STORAGE_KEYS = {
  CHECK_CONFIG: 'check_config',
  CURRENT_CHECK: 'current_check',
}

let dbInstance: IDBPDatabase<GasInspectionDB> | null = null

// 初始化数据库
export async function initDB(): Promise<IDBPDatabase<GasInspectionDB>> {
  if (dbInstance) return dbInstance

  dbInstance = await openDB<GasInspectionDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // 创建检查记录存储
      if (!db.objectStoreNames.contains('check_records')) {
        const checkRecordsStore = db.createObjectStore('check_records', {
          keyPath: 'id',
        })
        checkRecordsStore.createIndex('by-date', 'checkDate')
        checkRecordsStore.createIndex('by-unit', 'inspectedUnit')
      }

      // 创建报告存储
      if (!db.objectStoreNames.contains('reports')) {
        const reportsStore = db.createObjectStore('reports', {
          keyPath: 'id',
        })
        reportsStore.createIndex('by-record-id', 'checkRecordId')
      }
    },
  })

  return dbInstance
}

// ==================== 检查记录操作 ====================

// 保存检查记录
export async function saveCheckRecord(record: CheckRecord): Promise<void> {
  const db = await initDB()
  await db.put('check_records', record)
}

// 获取检查记录
export async function getCheckRecord(id: string): Promise<CheckRecord | undefined> {
  const db = await initDB()
  return await db.get('check_records', id)
}

// 获取所有检查记录（按日期倒序）
export async function getAllCheckRecords(): Promise<CheckRecord[]> {
  const db = await initDB()
  const records = await db.getAll('check_records')
  return records.sort((a, b) =>
    new Date(b.checkDate).getTime() - new Date(a.checkDate).getTime()
  )
}

// 删除检查记录
export async function deleteCheckRecord(id: string): Promise<void> {
  const db = await initDB()
  await db.delete('check_records', id)

  // 同时删除关联的报告
  const reports = await db.getAllFromIndex('reports', 'by-record-id', id)
  for (const report of reports) {
    await db.delete('reports', report.id)
  }
}

// ==================== 报告操作 ====================

// 保存报告
export async function saveReport(report: Report): Promise<void> {
  const db = await initDB()
  await db.put('reports', report)
}

// 获取报告
export async function getReport(id: string): Promise<Report | undefined> {
  const db = await initDB()
  return await db.get('reports', id)
}

// 根据检查记录ID获取报告
export async function getReportByRecordId(recordId: string): Promise<Report | undefined> {
  const db = await initDB()
  const reports = await db.getAllFromIndex('reports', 'by-record-id', recordId)
  return reports[0]
}

// ==================== LocalStorage 操作 ====================

// 保存检查配置
export function saveCheckConfig(config: CheckConfig): void {
  localStorage.setItem(STORAGE_KEYS.CHECK_CONFIG, JSON.stringify(config))
}

// 获取检查配置
export function getCheckConfig(): CheckConfig | null {
  const configStr = localStorage.getItem(STORAGE_KEYS.CHECK_CONFIG)
  if (!configStr) return null

  try {
    return JSON.parse(configStr) as CheckConfig
  } catch {
    return null
  }
}

// 清除检查配置
export function clearCheckConfig(): void {
  localStorage.removeItem(STORAGE_KEYS.CHECK_CONFIG)
}

// 保存当前检查状态
export function saveCurrentCheck(state: CurrentCheckState): void {
  localStorage.setItem(STORAGE_KEYS.CURRENT_CHECK, JSON.stringify(state))
}

// 获取当前检查状态
export function getCurrentCheck(): CurrentCheckState | null {
  const stateStr = localStorage.getItem(STORAGE_KEYS.CURRENT_CHECK)
  if (!stateStr) return null

  try {
    return JSON.parse(stateStr) as CurrentCheckState
  } catch {
    return null
  }
}

// 清除当前检查状态
export function clearCurrentCheck(): void {
  localStorage.removeItem(STORAGE_KEYS.CURRENT_CHECK)
}

// ==================== 部门配置操作 ====================

const DEPT_CONFIG_KEY = 'department_config'

export function saveDepartmentConfig(config: DepartmentConfig): void {
  localStorage.setItem(DEPT_CONFIG_KEY, JSON.stringify(config))
}

export function getDepartmentConfig(): DepartmentConfig | null {
  const str = localStorage.getItem(DEPT_CONFIG_KEY)
  if (!str) return null
  try {
    return JSON.parse(str) as DepartmentConfig
  } catch {
    return null
  }
}

// ==================== 工具函数 ====================

// 清空所有数据
export async function clearAllData(): Promise<void> {
  const db = await initDB()

  // 清空 IndexedDB
  await db.clear('check_records')
  await db.clear('reports')

  // 清空 LocalStorage
  clearCheckConfig()
  clearCurrentCheck()
}

// 导出所有数据
export async function exportAllData(): Promise<{
  records: CheckRecord[]
  reports: Report[]
}> {
  const db = await initDB()
  const records = await db.getAll('check_records')
  const reports = await db.getAll('reports')

  return { records, reports }
}
